import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Box,
    Button,
    List,
    ListItemText,
    Typography
} from "@mui/material";
import React, {useContext, useState} from "react";
import RemitterForm from "./RemitterForm";
import RemitteeForm from "./RemitteeForm";
import AccountantForm from "./AccountantForm";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {CBContext} from "./CBContext";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Formik hands back {field: message}; nested shapes are flattened so nothing is lost.
function collectMessages(formikErrors) {
    const messages = [];
    const walk = (value) => {
        if (!value) return;
        if (typeof value === 'string') messages.push(value);
        else if (Array.isArray(value)) value.forEach(walk);
        else if (typeof value === 'object') Object.values(value).forEach(walk);
    };
    walk(formikErrors);
    return [...new Set(messages)];
}

export default function Entity() {
    const {cbState, cbDispatch} = useContext(CBContext);

    const remitterRef = cbState.baseData.remitterRef;
    const remitteeRef = cbState.baseData.remitteeRef;
    const accountantRef = cbState.baseData.accountantRef;
    const [errors, setErrors] = useState(null);
    const [expanded, setExpanded] = useState({remitter: false, remittee: false, accountant: false});

    function toggleSection(section) {
        return (event, isExpanded) => setExpanded(current => ({...current, [section]: isExpanded}));
    }

    function handleEntitySave() {
        let remitter = remitterRef.current.validateForm();
        let remittee = remitteeRef.current.validateForm();
        let accountant = accountantRef.current.validateForm();

        Promise.all([remitter, remittee, accountant]).then((res) => {
            const sections = [
                {key: 'remitter', label: 'Remitter', messages: collectMessages(res[0])},
                {key: 'remittee', label: 'Remittee', messages: collectMessages(res[1])},
                {key: 'accountant', label: 'Accountant', messages: collectMessages(res[2])},
            ];
            const failed = sections.filter(section => section.messages.length > 0);

            if (failed.length === 0) {
                setErrors(null);
                if (cbState.baseData.status === 0){
                    cbDispatch({
                        type: 'addFormData',
                        data: {
                            'remitter': remitterRef.current.values,
                            'remittee': remitteeRef.current.values,
                            'accountant': accountantRef.current.values,
                        }
                    });
                }
                cbState.baseData.setActiveStep(1, 0);
            } else {
                // Name the fields and open the sections holding them - a collapsed
                // section used to hide the only clue about what was wrong.
                setErrors(failed);
                setExpanded(current => ({
                    ...current,
                    ...Object.fromEntries(failed.map(section => [section.key, true]))
                }));
            }
        })

    }

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                flexDirection: 'column'
            }}
        >
            {
                errors && <Alert
                    severity={'error'}
                    icon={false}
                    onClose={() => setErrors(null)}
                >
                    <AlertTitle>Fix the following before continuing</AlertTitle>
                    <List disablePadding={true}>
                        {errors.map(section => (
                            <ListItemText key={section.key}>
                                <b>{section.label}</b>: {section.messages.join('; ')}
                            </ListItemText>
                        ))}
                    </List>

                </Alert>
            }
            <Accordion expanded={expanded.remitter} onChange={toggleSection('remitter')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={'h5'}>Remitter</Typography>
                </AccordionSummary
                >
                <AccordionDetails>
                    <RemitterForm/>
                </AccordionDetails>
            </Accordion>
            <Accordion expanded={expanded.remittee} onChange={toggleSection('remittee')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={'h5'}>Remittee</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <RemitteeForm/>
                </AccordionDetails>
            </Accordion>
            <Accordion expanded={expanded.accountant} onChange={toggleSection('accountant')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography variant={'h5'}>Accountant</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <AccountantForm/>
                </AccordionDetails>
            </Accordion>
            <Box
                sx={{
                    textAlign: 'center'
                }}
            >
                <Button
                    variant={'contained'}
                    endIcon={<NavigateNextIcon/>}
                    onClick={handleEntitySave}
                >
                    Next
                </Button>
            </Box>
        </Box>
    )
}